<template>
    <v-container fluid class="b-container">
        <v-row>
            <v-col cols="12">
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="112"
                ></v-skeleton-loader>

                <v-card v-else tile outlined class="py-6">
                    <v-row
                        no-gutters
                        justify="space-between"
                        align="center"
                        class="px-5"
                    >
                        <v-col cols="auto">
                            <v-card-title class="pa-0">{{
                                statistics.name
                            }}</v-card-title>
                            <h4
                                v-if="store.active"
                                class="subtitle-1 success--text"
                                v-text="translations.online[lang]"
                            ></h4>
                            <h4
                                v-else
                                class="subtitle-1 error--text"
                                v-text="translations.offline[lang]"
                            ></h4>
                        </v-col>

                        <v-col
                            v-for="(statistic, index) in titleStatistics"
                            :key="statistic.text['en']||'s' + index"
                            cols="auto"
                        >
                            <v-card-title class="pa-0 justify-center">{{
                                statistic.value
                            }}</v-card-title>
                            <v-row
                                no-gutters
                                align="center"
                                class="subtitle-1 text-center text--secondary"
                            >
                                <v-icon
                                    v-text="statistic.icon"
                                    class="mr-1"
                                ></v-icon>
                                {{ statistic.text[lang] }}
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>

            <v-col
                v-for="(view, ind) in views"
                v-bind:key="view.text['en']||'v' + ind"
                cols="12"
                sm="6"
                lg="4"
            >
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="100"
                ></v-skeleton-loader>

                <v-card v-else tile outlined class="pa-5">
                    <v-row no-gutters justify="space-between" align="center">
                        <v-col cols="auto">
                            <v-avatar color="accent" size="60">
                                <v-icon dark large v-text="view.icon"></v-icon>
                            </v-avatar>
                        </v-col>

                        <v-col class="text-right">
                            <h4
                                class="subtitle-1 font-weight-bold"
                                v-text="view.value"
                            ></h4>
                            <h4
                                class="
                                    subtitle-2
                                    font-weight-medium
                                    text--secondary
                                "
                                v-text="view.text[lang]"
                            ></h4>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>

            <v-col
                v-for="(transaction, i) in transactions"
                :key="transaction.text[lang]||'t' + i"
                cols="12"
                md="6"
            >
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                    height="100"
                ></v-skeleton-loader>

                <v-card v-else tile outlined class="pa-5">
                    <v-row no-gutters justify="space-between" align="center">
                        <v-col cols="auto">
                            <v-avatar color="accent" size="60">
                                <v-icon
                                    dark
                                    large
                                    v-text="transaction.icon"
                                ></v-icon>
                            </v-avatar>
                        </v-col>

                        <v-col cols="auto" class="text-right">
                            <h4
                                class="subtitle-1 font-weight-bold"
                                v-text="transaction.value"
                            ></h4>
                            <h4
                                class="
                                    subtitle-2
                                    font-weight-medium
                                    text--secondary
                                "
                                v-text="transaction.text[lang]"
                            ></h4>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-skeleton-loader
                    v-if="loading"
                    type="image"
                ></v-skeleton-loader>

                <v-card v-else tile outlined>
                    <v-card-title
                        v-text="translations.transactions[lang]"
                    ></v-card-title>

                    <v-divider></v-divider>

                    <v-card-text>
                        <canvas ref="myChart"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import {
    mdiAccountGroup,
    mdiDatabase,
    mdiCurrencyEur,
    // mdiBinoculars,
    mdiCursorDefault,
    mdiCompassOutline,
    mdiPhone,
    mdiWallet,
    mdiTrophyVariant,
    mdiCellphoneIphone,
} from "@mdi/js";

import Chart from "chart.js";
import moment from "moment";

import { mapActions, mapState } from "vuex";
import translations from "@/utils/translations/home";

export default {
    name: "StorePanelHome",

    mixins: [translations],

    data: () => ({
        labels: [],
        values: [],
    }),

    computed: {
        ...mapState("storePanel", ["statistics", "store"]),
        ...mapState(["permissions", "superUser"]),

        lang() {
            return this.$route.params.lang;
        },

        permission() {
            return this.$store.state.permissions.homepage
                ? this.$store.state.permissions.homepage.read
                : this.superUser;
        },

        loading() {
            return this.$store.state.storePanel.loading;
        },

        titleStatistics() {
            return [
                {
                    icon: mdiAccountGroup,
                    text: {
                        el: "Πελάτες",
                        en: "Customers",
                        it: "Customers",
                    },
                    value: this.statistics.total_customers,
                },
                {
                    icon: mdiDatabase,
                    text: {
                        el: "Συναλλαγές",
                        en: "Transactions",
                        it: "Transazioni",
                    },
                    value: this.statistics.total_transactions,
                },
                {
                    icon: mdiCurrencyEur,
                    text: {
                        el: "Τζίρος",
                        en: "Total Revenue",
                        it: "Totale",
                    },
                    value: new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                    }).format(this.statistics.total_income),
                },
                {
                    icon: mdiTrophyVariant,
                    text: {
                        el: "Σύνολο πόντων",
                        en: "Total Points",
                        it: "Punti totali",
                    },
                    value: this.statistics.total_points,
                },
            ];
        },

        views() {
            return [
                // {
                //     icon: mdiBinoculars,
                //     text: {
                //         el: "",
                //         en: "User map views",
                //         it: "",
                //     },
                //     value: this.statistics.views.map_views,
                // },
                {
                    icon: mdiCursorDefault,
                    text: {
                        el: "Επισκέψεις καταστήματος",
                        en: "User store clicks",
                        it: "Visite in negozio",
                    },
                    value: this.statistics.views
                        ? this.statistics.views.visits
                        : "",
                },
                {
                    icon: mdiCompassOutline,
                    text: {
                        el: "Επισκέψεις από Πλοήγηση",
                        en: "User navigation clicks",
                        it: "Visite dalla navigazione",
                    },
                    value: this.statistics.views
                        ? this.statistics.views.nav_clicks
                        : "",
                },
                {
                    icon: mdiCellphoneIphone,
                    text: {
                        el: "Επισκέψεις από κινητό",
                        en: "User phone clicks",
                        it: "Visite mobili",
                    },
                    value: this.statistics.views
                        ? this.statistics.views.phone_clicks
                        : "",
                },
            ];
        },

        transactions() {
            return [
                {
                    icon: mdiWallet,
                    text: {
                        el: "Τελευταίο 12ωρο",
                        en: "Payments Last 12 Hours",
                        it: "Ultime 12Ore",
                    },
                    value: `
                        ${
                            this.statistics.last_twelve_hours_payments
                                ? this.statistics.last_twelve_hours_payments
                                      .total_transactions
                                : ""
                        }
                        ${this.translations.transactions[this.lang]} /
                        ${new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                        }).format(
                            this.statistics.last_twelve_hours_payments
                                ? this.statistics.last_twelve_hours_payments
                                      .total_price
                                : ""
                        )}
                        ${this.translations.total[this.lang]}
                    `,
                },
                {
                    icon: mdiWallet,
                    text: {
                        el: "Τελευταίες 7 ημέρες",
                        en: "Payments Last Week",
                        it: "Ultimi 7 giorni",
                    },
                    value: `
                        ${
                            this.statistics.last_week_payments
                                ? this.statistics.last_week_payments
                                      .total_transactions
                                : ""
                        }
                        ${this.translations.transactions[this.lang]} /
                        ${new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                        }).format(
                            this.statistics.last_week_payments
                                ? this.statistics.last_week_payments.total_price
                                : ""
                        )}
                        ${this.translations.total[this.lang]}
                    `,
                },
            ];
        },
    },

    methods: {
        ...mapActions("storePanel", ["getStatistics"]),

        createChart() {
            if (this.$refs.myChart) {
                let ctx = this.$refs.myChart.getContext("2d");

                var gradientStroke = ctx.createLinearGradient(
                    500,
                    -100,
                    100,
                    0
                );
                gradientStroke.addColorStop(0, "#1feaea");
                gradientStroke.addColorStop(0.33, "yellow");
                gradientStroke.addColorStop(0.66, "orange");
                gradientStroke.addColorStop(1, "red");

                var myChart = new Chart(ctx, {
                    type: "line",
                    data: {
                        labels: this.labels,
                        datasets: [
                            {
                                label: "",
                                data: this.values,
                                backgroundColor: ["rgba(0, 0, 0, 0)"],
                                borderColor: gradientStroke,
                                pointBorderColor: gradientStroke,
                                pointBackgroundColor: gradientStroke,
                                pointHoverBackgroundColor: gradientStroke,
                                pointHoverBorderColor: gradientStroke,
                                borderWidth: 4,
                            },
                        ],
                    },
                    options: {
                        legend: {
                            position: "top",
                        },
                        scales: {
                            yAxes: [
                                {
                                    ticks: {
                                        fontColor: "rgba(0,0,0,0.5)",
                                        fontStyle: "bold",
                                        beginAtZero: true,
                                        maxTicksLimit: 5,
                                        padding: 20,
                                    },
                                    gridLines: {
                                        drawTicks: false,
                                    },
                                },
                            ],
                            xAxes: [
                                {
                                    gridLines: {
                                        zeroLineColor: "transparent",
                                    },
                                    ticks: {
                                        padding: 20,
                                        fontColor: "rgba(0,0,0,0.5)",
                                        fontStyle: "bold",
                                    },
                                },
                            ],
                        },
                    },
                });
            }
        },
    },

    watch: {
        permission(val) {
            if (!val) {
                this.$router.replace(
                    `/${this.lang}/storePanel/forbidden-gateway`
                );
            }
        },

        ["$store.state.storePanel.statistics"]: {
            immediate: true,
            handler(val) {
                if (val.last_seven_days_revenue) {
                    if (!this.labels.length) {
                        val.last_seven_days_revenue.forEach((r) =>
                            this.labels.push(
                                moment(r.date).format("DD/MM/YYYY")
                            )
                        );

                        this.labels.reverse();
                    }

                    if (!this.values.length) {
                        val.last_seven_days_revenue.forEach((r) =>
                            this.values.push(r.total_price)
                        );

                        this.values.reverse();
                    }
                }
            },
        },
         ["store.store_id"]: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.getStatistics();
                }
            },
        },
    },

    mounted() {
        setTimeout(() => this.createChart(), 2000);
    },
};
</script>
